import { useRef, useState } from "react";

import { utcToZonedTime } from "date-fns-tz";
import { getYear, getWeek } from "date-fns";

import queryString from "query-string";

import { clone } from "lodash";
import { ONE_MINUTE_OF_MS } from "reports/utils/timeUtils";
import {
  DataDumpAggregateReportsType,
  SimplifiedShiftSegment,
  TimeRangeFinite,
} from "../../../types";
import { useGetShifts } from "reports/utils/shiftsHook";
import {
  generateEmptyAggregateData,
  sumDataDumpAggregatesExperimental,
  unflat,
  getWeekAndDayTimeRanges,
} from "reports/pages/utils";
import DataDumpGetter from "../../../getters/DataDumpGetter";
import WeekToDate from "./WeekToDate";
import LoadingCircle from "reports/components/simple/LoadingCircle";
import {
  WeekToDateProps,
  ManualDataAdjustmentForAggregateType,
} from "./WeekToDateTypes";
import { useParams } from "react-router";

export default function WeekToDatePipeline({
  machemetryId,
  targets,
  shifts,
  processName,
  unitsProducedPerMs,
  manualData,
  timeZone,
  shiftsPerWeek,
  activeShifts,
  staticOeeTarget,
  shiftsToSkip = ["Not Scheduled"],
  shouldShowTranslate = false,
}: WeekToDateProps) {
  /* Auto-refresh page every 5 minutes */
  setTimeout(function () {
    window.location.reload();
  }, 5 * ONE_MINUTE_OF_MS);

  const { year, week } = useParams<{ year?: string; week?: string }>();

  const [dataByShiftFromGetter, setDataByShiftFromGetter] = useState<
    DataDumpAggregateReportsType[]
  >([]);

  let shouldShowCloseTab = false;

  if (location.search !== "") {
    const values = queryString.parse(location.search);
    shouldShowCloseTab = Boolean(values.s && values.s === "eol");
  }

  const now = useRef(Date.now().valueOf());
  const zonedNow = utcToZonedTime(
    new Date(now.current).toISOString(),
    timeZone
  );

  const startTimeString = shifts?.[0]?.startTime;

  const [startHour, startMinute] = startTimeString
    ? startTimeString.split(":")
    : [0, 0];

  const { dayStartHour, dayStartMinute } = {
    dayStartHour: Number(startHour),
    dayStartMinute: Number(startMinute),
  };

  const dateTimeContext = {
    timeZone,
    year: year && week ? parseInt(year, 10) : getYear(zonedNow),
    weekNumber: year && week ? parseInt(week, 10) : getWeek(zonedNow),
    dayStartHour,
    dayStartMinute,
  };
  const timeRanges = getWeekAndDayTimeRanges(dateTimeContext);

  const shiftsData = useGetShifts(timeRanges.week, machemetryId).filter(
    shiftSegment =>
      shiftSegment.shift.name && !shiftsToSkip.includes(shiftSegment.shift.name)
  );

  // place the function inside because we need contextual information such as units produced per ms of runtime
  const manualDataDumpAggregateChange = (
    dataDumpAggregateFromGetter: DataDumpAggregateReportsType,
    manualDatas: ManualDataAdjustmentForAggregateType[]
  ) => {
    const manualDatum = manualDatas.find(({ ge, lt }) => {
      return (
        +dataDumpAggregateFromGetter.ge <= ge &&
        +dataDumpAggregateFromGetter.lt >= lt
      );
    });

    const newShiftData = clone(dataDumpAggregateFromGetter);

    newShiftData.productionTime += manualDatum ? manualDatum.time : 0;

    newShiftData.productionPotentialOutput += manualDatum
      ? manualDatum.time * unitsProducedPerMs
      : 0;
    newShiftData.netOutput += manualDatum ? manualDatum.count : 0;

    newShiftData.oeePercent =
      newShiftData.productionPotentialOutput !== 0
        ? 100 *
          (newShiftData.netOutput / newShiftData.productionPotentialOutput)
        : 0;

    return newShiftData;
  };

  const manualDataModifiedDataByShift =
    manualData?.length > 0
      ? dataByShiftFromGetter.map(shiftData => {
          return manualDataDumpAggregateChange(shiftData, manualData);
        })
      : dataByShiftFromGetter;

  const dataByDay = timeRanges.days.map(({ ge, lt }, index) => {
    const dataByShiftInDay =
      activeShifts !== 1
        ? manualDataModifiedDataByShift.filter(
            // filter for only data inside the timeRange
            data => {
              return Number(data.ge) >= ge && Number(data.lt) <= lt;
            }
          )
        : manualDataModifiedDataByShift[index] && [
            manualDataModifiedDataByShift[index], // having one shift a day means we can just put in one aggregate a day
          ];

    return dataByShiftInDay
      ? sumDataDumpAggregatesExperimental(dataByShiftInDay)
      : generateEmptyAggregateData();
  });

  const dataByWeek = dataByDay
    ? [sumDataDumpAggregatesExperimental(dataByDay)]
    : [];

  const shiftRanges: SimplifiedShiftSegment[] | undefined = shiftsData?.map(
    ({ range, shift }) => ({
      ge: Number(range.ge),
      lt: Number(range.lt),
      name: shift.name,
    })
  );

  /* the next two lines of code filters out for unique shift names */
  const shiftDataNames = shiftsData?.map(entry => {
    return entry.shift.name;
  });

  const uniqueShiftNames = shiftDataNames?.filter((entry, index, self) => {
    return self.indexOf(entry) === index;
  });

  // we get the first index of the value that we filter to find the oee target for the week
  const oeeTargetArray = targets.filter(entry => {
    return (
      entry.year === Number(dateTimeContext.year) &&
      entry.week === Number(dateTimeContext.weekNumber)
    );
  });

  const oeeTarget =
    oeeTargetArray.length > 0 ? oeeTargetArray[0].oeeTarget : staticOeeTarget;

  timeRanges.shifts = unflat(shiftRanges, activeShifts); // shifts are not initially filled, we fill them with shift data acquired from query

  return (
    <>
      {shiftRanges?.map(shiftTime => (
        <DataDumpGetter
          key={shiftTime.ge}
          isAggregate={true}
          multiData={true}
          setData={setDataByShiftFromGetter}
          range={shiftTime as TimeRangeFinite}
          machemetryIds={[machemetryId]}
        />
      ))}
      {shiftsData &&
      dataByShiftFromGetter.length >= shiftsPerWeek &&
      shifts &&
      shifts.length > 0 ? ( // we want our data to be complete before displaying
        <WeekToDate
          dateTimeContext={dateTimeContext}
          timeRanges={timeRanges}
          uniqueShiftName={uniqueShiftNames}
          dataByShift={manualDataModifiedDataByShift}
          dataByDay={dataByDay}
          dataByWeek={dataByWeek}
          dataRetrievedAt={now.current}
          processName={processName}
          oeeTargetPercent={oeeTarget}
          shouldShowCloseTab={shouldShowCloseTab}
          shouldShowTranslate={shouldShowTranslate}
        />
      ) : (
        <LoadingCircle />
      )}
    </>
  );
}
